import { Button } from "../../../../components/common/Button";
import { ContentWrapper, ItemWrapper } from "../styles";
import React, { useContext, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import Loader from "../../../../components/common/Loader";
import WalletOTP from "../../components/SendFunds/components/WalletOTP";
import {
  DetailsBox,
  DetailsContent,
  DetailsRow,
  DetailsValue,
} from "../../Wallet/ChinaPayoutV2/styles";
import {
  DetailsLabel,
  DetailsSectionHeader,
  EditButton,
  SectionTitle,
} from "../../components/Withdrawal/styles";
import { formatCurrencyAmount } from "../../../../utils/currency";
import { formatValue, getErrorMessage } from "../../utils";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import dayjs from "dayjs";
import { clearUserData, setStep } from "../../redux/formDataSlice";
import { capitalizeCurrencyCode, isValidMinimumAmount } from "../../../../utils/helpers";
import { GHS_MINIMUM_PAYOUT_AMOUNT } from "./constants";
import { useNavigate, useParams } from "react-router-dom";
import EventFactory from "../../../../utils/eventFactory";
import { merchantBusinessKeys } from "../../../Settings/settingsSlice";
import { sendFromWalletAction } from "../../redux/slice";
import { requestPayoutV2Otp } from "../../api";
import { SuccessAlertContext } from "../../../../context/SuccessAlertContext";
import { NetworkErrorAlertContext } from "../../../../context/NetworkErrorAlert";
import { encryptPayloadWithMerchantKeyJIT } from "../../../../utils/encryptionAlgorithm";

type ReviewTypes = {
  handlePrevious: () => void;
};

type ReviewState = {
  publicKey: string;
  taxRefId: string;
  loading: boolean;
  verifyOTP: boolean;
};

const Review = ({ handlePrevious }: ReviewTypes) => {
  const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);
  const { businessId } = useAppSelector((state) => state.users);
  const { loading } = useAppSelector((state) => state.wallets);
  const amount = useAppSelector((state) => state.formData.payoutv2Data?.amount);
  const fees = useAppSelector((state) => state.formData.payoutv2Data?.fees);
  const totalAmountCharged = (Number(amount) || 0) + (Number(fees) || 0);

  const { currencyCode } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const eventFactory = EventFactory.getInstance();
  const PHONE_NUMBER = getSavedData?.phoneNumber?.replace("+", "");

  const successAlertContext = useContext(SuccessAlertContext);

  const { onShowAlert: onSuccess } = successAlertContext || {};
  const { onShowAlert } = useContext(NetworkErrorAlertContext);

  const currentDate = dayjs().format("YYYY-MM-DD");

  const [reviewData, setReviewData] = useState<ReviewState>({
    publicKey: "",
    taxRefId: "",
    loading: false,
    verifyOTP: false,
  });

  const updateReviewData = (fields: Partial<ReviewState>) =>
    setReviewData((prev) => ({ ...prev, ...fields }));

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId))?.then(({ payload }) => {
        updateReviewData({
          publicKey: payload?.data?.publicKey ?? "",
        });
      });
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    updateReviewData({ taxRefId: `tranf-${uuidv4()}` });
  }, []);

  const trackPayout = (status: "success" | "failed", errorMessage?: string) => {
    eventFactory.trackPayout(
      amount,
      capitalizeCurrencyCode(currencyCode),
      "bank_transfer",
      status,
      errorMessage,
    );
  };

  const handleSendOTP = async () => {
    updateReviewData({ loading: true });
    try {
      await requestPayoutV2Otp(reviewData.taxRefId, reviewData.publicKey);
      updateReviewData({ verifyOTP: true, loading: false });
      eventFactory.captureEvent("payout_otp", {
        status: "success",
        currency: capitalizeCurrencyCode(currencyCode),
      });
    } catch (err: any) {
      updateReviewData({ loading: false });
      const message = getErrorMessage(err, "Failed to send OTP");
      onShowAlert?.("", message);
      eventFactory.captureEvent("payout_otp", {
        status: "failure",
        failure_reason: message,
        currency: capitalizeCurrencyCode(currencyCode) || "",
      });
    }
  };



  const handlePayout = async (otp?: string) => {
    if (!isValidMinimumAmount(amount, GHS_MINIMUM_PAYOUT_AMOUNT)) {
      onShowAlert?.(
        "",
        `Minimum amount: ${capitalizeCurrencyCode("GHS")} ${new Intl.NumberFormat().format(GHS_MINIMUM_PAYOUT_AMOUNT)}`,
      );
      return;
    }

    try {
      if (!businessId) {
        onShowAlert?.("", "Business information is not available. Please try again.");
        return;
      }

      const basePayload = {
        country: "GH",
        amount: Number(amount),
        accountName: getSavedData?.accountName,
        currency: capitalizeCurrencyCode(currencyCode),
        requestId: reviewData.taxRefId,
        description: getSavedData?.paymentDescription,
        otp,
      };

      const isBank = getSavedData.paymentMethod?.includes("BANK_ACCOUNT");

      const payloadData = isBank
        ? {
          ...basePayload,
          bankName: getSavedData.bankName,
          accountNumber: getSavedData?.accountNumber,
          bankCode: getSavedData.bankCode,
          message: "bypass",
          bankAddress: getSavedData.bankAddress
        }
        : {
          ...basePayload,
          bankName: getSavedData.bankName,
          serviceCode: getSavedData.bankCode,
          accountNumber: PHONE_NUMBER,
        };

      const { encryptedMessage, publicKey } = await encryptPayloadWithMerchantKeyJIT(
        businessId,
        payloadData,
      );
      const payload = { message: encryptedMessage };
      const token = publicKey || reviewData.publicKey;

      const actionResult = dispatch(
        sendFromWalletAction({ data: payload, token }),
      );

      actionResult.then((result) => {
        if (sendFromWalletAction.fulfilled.match(result)) {
          onSuccess?.("Payout successfully sent");
          trackPayout("success");
          dispatch(clearUserData());
          navigate(`/wallets/`);
        } else if (sendFromWalletAction.rejected.match(result)) {
          onShowAlert?.("", (result.payload as string) ?? "Failed to send from wallet.");
          trackPayout("failed", (result.payload as string) ?? result.error.message);
        }
      });
    } catch (err: any) {
      const message = getErrorMessage(err, "Failed to initiate payout");
      onShowAlert?.("", message);
      trackPayout("failed", message);
    }
  };

  const paymentType = getSavedData?.paymentMethod === "MOBILE_MONEY"
    ? getSavedData?.bankName
    : getSavedData?.paymentMethodName;


  return (
    <ContentWrapper>
      <Loader isLoading={reviewData.loading || loading} />

      {reviewData.verifyOTP && (
        <WalletOTP
          setVerifyOTP={(v) => updateReviewData({ verifyOTP: v })}
          makePayment={handlePayout}
          mode="transaction"
          onResend={handleSendOTP}
        />
      )}

      <DetailsBox>
        <DetailsSectionHeader>
          <SectionTitle>Transaction details</SectionTitle>
          <EditButton onClick={() => dispatch(setStep(0))}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Amount sent</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount("GHS", amount)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Processing fee</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount("GHS", fees)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Total amount charged</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount("GHS", totalAmountCharged)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Date</DetailsLabel>
            <DetailsValue>{currentDate}</DetailsValue>
          </DetailsRow>
        </DetailsContent>

        <DetailsSectionHeader>
          <SectionTitle>Beneficiary details</SectionTitle>
          <EditButton onClick={() => dispatch(setStep(1))}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Payment type</DetailsLabel>
            <DetailsValue>
              {paymentType ? formatValue(paymentType) : "N/A"}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Beneficiary name</DetailsLabel>
            <DetailsValue>
              {getSavedData?.accountName
                ? formatValue(getSavedData.accountName)
                : "N/A"}
            </DetailsValue>
          </DetailsRow>

          {getSavedData?.paymentMethod?.includes("BANK_ACCOUNT") && (
            <DetailsRow>
              <DetailsLabel>Bank name</DetailsLabel>
              <DetailsValue>
                {getSavedData?.bankName
                  ? formatValue(getSavedData.bankName)
                  : "N/A"}
              </DetailsValue>
            </DetailsRow>
          )}

          {getSavedData?.paymentMethod?.includes("BANK_ACCOUNT") && (
            <DetailsRow>
              <DetailsLabel>Bank address</DetailsLabel>
              <DetailsValue>
                {getSavedData?.bankAddress
                  ? formatValue(getSavedData.bankAddress)
                  : "N/A"}
              </DetailsValue>
            </DetailsRow>
          )}

          <DetailsRow>
            <DetailsLabel>
              {getSavedData?.paymentMethod?.includes("BANK_ACCOUNT")
                ? "Account number"
                : "Phone number"}
            </DetailsLabel>
            <DetailsValue>
              {getSavedData?.accountNumber ?? PHONE_NUMBER ?? "N/A"}
            </DetailsValue>
          </DetailsRow>
        </DetailsContent>
      </DetailsBox>

      <ItemWrapper>
        <Button
          onClick={handlePrevious}
          label="Prev"
          height="48px"
          theme="alternate"
          width="203px"
        />

        <Button
          label="Continue"
          height="48px"
          onClick={handleSendOTP}
        />
      </ItemWrapper>
    </ContentWrapper>
  );
};

export default Review;
